import { createFileRoute } from "@tanstack/react-router";
import { GuideStrip } from "../components/guide-strip";
import { Phone, Check, Clock, ShieldCheck, Star, Sparkles, Droplets, Hammer, Snowflake, ThermometerSun, Calendar, AlertTriangle, Leaf } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { SHOW_LICENSED_INSURED } from "../data/flags";
import {
  SocialProofStrip,
  ValueStack,
  GuaranteeCallout,
  ScarcityPill,
  FaqJsonLd,
} from "../components/special-landing-extras";

import { StampedGallerySection } from "../components/stamped-gallery";
import concreteCleaningHero from "../assets/service-heroes/service-concrete-cleaning-sealing-v2.webp.asset.json";
import beading1 from "../assets/concrete-sealed/sealed-water-beading-1.jpg.asset.json";
import beading2 from "../assets/concrete-sealed/sealed-water-beading-2.jpg.asset.json";
import spallingImg from "../assets/concrete-damage/concrete-spalling.webp";
import pittingImg from "../assets/concrete-damage/concrete-pitting.webp";
import saltScalingImg from "../assets/concrete-damage/concrete-salt-scaling.webp";
import { InlineQuoteSection } from "@/components/inline-quote-section";
import { TopQuoteForm } from "@/components/top-quote-form";

const SPECIAL_ID = "concrete-sealing-freeze-thaw";

const FAQS = [
  {
    q: "Will sealing change the look of my concrete?",
    a: "No. Trident Break Wall is a penetrating, invisible sealer. It soaks in below the surface and leaves no glossy film, no wet-looking coating, and no color change. Your concrete looks the same — it just repels water and salt.",
  },
  {
    q: "Does sealing stop mold, moss, and algae on concrete?",
    a: "It inhibits it. Mold, moss, and algae need trapped moisture to grow. By sealing the concrete and reducing how much water soaks in, the surface stays drier and the organic film has a much harder time taking hold. You still get some buildup over time, but cleanings become easier and less frequent.",
  },
  {
    q: "Can you fix concrete that's already spalling?",
    a: "We don't do resurfacing. If the surface is already flaking off, the damage is structural and needs repair or replacement. What we can do is remove the loose material, clean the slab, and seal the sound concrete that's left so the damage stops spreading. If the slab is too far gone, we'll tell you honestly.",
  },
  {
    q: "Is it too late in the season to seal?",
    a: "Fall is actually the ideal window — the slab can dry and the sealer can cure before winter arrives. Once temperatures are consistently below about 40°F and staying there, the surface won't cure properly. We'll check the forecast and tell you if the timing is right.",
  },
  {
    q: "How long before I can walk or drive on it?",
    a: "Fast. The surface is dry to the touch in about 60 minutes in normal conditions, and the area is typically open to foot and vehicle traffic in 2–4 hours depending on temperature and site conditions. We'll give you exact instructions before we leave.",
  },
  {
    q: "Does the concrete have to be completely dry before you seal?",
    a: "No. Trident Break Wall applies on dry or damp surfaces — it just can't have standing water or puddles. That's why we clean and seal the same day instead of making you wait for a second visit. A drier surface still promotes the best penetration, so we blow the slab off and let it flash dry before we start.",
  },
  {
    q: "Do you clean and seal in the same visit?",
    a: "Yes. Because the sealer can be applied to a damp surface, we complete the project same day — clean the slab, remove loose material, let it flash dry, then apply two coats wet-on-wet. One crew, one trip, done.",
  },
  {
    q: "How often should I reseal?",
    a: "Trident recommends reapplication every 5–8 years depending on exposure. In the Capital Region, with heavy road salt and freeze/thaw cycles, we find every 2–3 years is the practical maintenance cycle for most residential concrete. Driveways, aprons, and steps that get hit hard with salt may need it sooner. Annual cleaning in between keeps the sealer performing longer.",
  },
  {
    q: "What's the difference between Trident Break Wall and Sea Wall?",
    a: "Break Wall is our standard protection — a water-based, low-VOC silane/siloxane penetrating sealer rated for about 5 years of protection. Sea Wall is the premium upgrade — a solvent-based ultra water-repelling and salt-blocking sealer with Trident's EZ Nano technology, rated for about 7 years. Both are invisible and breathable. Sea Wall is the better choice for driveways, aprons, and steps that take the most road salt and plow traffic.",
  },
  {
    q: "Does it really stop salt damage?",
    a: "It dramatically reduces it. A quality penetrating sealer limits how much water and salt solution can enter the concrete. It can't prevent every crack from a moving slab, but it stops the surface-level freeze/thaw and salt scaling that destroys the finish.",
  },
  {
    q: "Does it work on stamped concrete?",
    a: "Yes. Stamped concrete is even more porous than standard flat concrete because of the textured surface. Sealing is essential to protect the color and detail. We also offer re-sealing and antiquing if the stamped pattern has faded.",
  },
  {
    q: "Can I combine the $50 off with another offer?",
    a: "One discount per household. If you're already using a military or first-responder discount, we'll apply whichever gives you the better value.",
  },
];

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Concrete Cleaning & Sealing — Freeze/Thaw Protection",
  provider: {
    "@type": "LocalBusiness",
    name: "Capital Pro Pressure Washing",
    telephone: "+1-518-900-1913",
    url: "https://capitalpropressurewashing.com",
    areaServed: "Albany, Schenectady, Clifton Park, Saratoga Springs, Troy, Rensselaer and surrounding Capital Region, NY",
  },
  serviceType: "Concrete cleaning and penetrating sealer application",
  areaServed: {
    "@type": "Place",
    name: "Capital Region, New York",
  },
  offers: {
    "@type": "Offer",
    description: "$50 off concrete cleaning and sealing when mentioned at booking.",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    validFrom: "2026-08-15",
  },
};

export const Route = createFileRoute("/concrete-sealing-freeze-thaw")({
  head: () => ({
    meta: [
      { title: "Concrete Sealing Freeze/Thaw Protection | Albany & Capital Region NY" },
      {
        name: "description",
        content:
          "Protect your driveway, walkway, and steps from freeze/thaw salt damage with professional concrete cleaning and sealing. Trident Break Wall penetrating sealer. Albany, Capital Region NY.",
      },
      { property: "og:title", content: "Concrete Sealing Freeze/Thaw Protection | Albany & Capital Region NY" },
      {
        property: "og:description",
        content:
          "Stop salt, spalling, and freeze/thaw damage before winter. Professional concrete cleaning + sealing across the Capital Region.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://capitalpropressurewashing.com/concrete-sealing-freeze-thaw" },
    ],
    links: [
      { rel: "canonical", href: "https://capitalpropressurewashing.com/concrete-sealing-freeze-thaw" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(SERVICE_SCHEMA),
      },
    ],
  }),
  component: LandingPage,
});

function LandingPage() {
  return (
    <main>
      <FaqJsonLd faqs={FAQS} />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-[var(--brand-deep-blue)]">
        <div aria-hidden="true" className="absolute inset-0 -z-20 overflow-hidden">
          <img
            src={concreteCleaningHero.url}
            alt=""
            className="h-full w-full object-cover opacity-60"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--brand-deep-blue)]/85 via-[var(--brand-deep-blue)]/75 to-[var(--brand-deep-blue)]/95" />
        </div>

        <div className="mx-auto max-w-5xl px-4 py-14 text-white sm:py-20">
          <ScarcityPill className="mb-5" />

          <p className="mb-3 font-[var(--font-display)] text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--brand-red)]">
            Fall Sealing Window — Capital Region NY
          </p>

          <h1 className="font-[var(--font-display)] text-4xl font-bold uppercase leading-[1.05] tracking-tight sm:text-6xl">
            Seal Your Concrete
            <br className="hidden sm:block" /> Before The Freeze
          </h1>

          <p className="mt-4 max-w-2xl text-lg text-white/85 sm:text-xl">
            Road salt and freeze/thaw cycles destroy unsealed driveways, walkways, and steps every winter. Cleaning and sealing now
            stops water from getting in — and keeps the surface you have from flaking apart.
          </p>

          {/* Offer card */}
          <div className="mt-8 inline-flex flex-col rounded-xl border-2 border-[color:var(--brand-red)] bg-white/5 p-5 backdrop-blur">
            <span className="text-xs font-semibold uppercase tracking-wider text-white/70">Concrete Cleaning & Sealing</span>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="font-[var(--font-display)] text-4xl font-bold text-white sm:text-5xl">$50 Off</span>
              <span className="text-sm text-white/70">mention when you book</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => openQuoteModal({ services: ["concrete"], message: "Interested in the $50 off concrete cleaning & sealing freeze/thaw special." })}
              className="inline-flex items-center justify-center rounded-md bg-[color:var(--brand-red)] px-7 py-4 text-base font-bold uppercase tracking-wide text-white shadow-lg transition hover:brightness-110 active:scale-[0.98]"
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-7 py-4 text-base font-bold uppercase tracking-wide text-[var(--brand-deep-blue)] shadow-lg transition hover:bg-white/90"
              data-analytics="call_click"
              data-source="concrete-freeze-thaw-hero"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              (518) 900-1913
            </a>
          </div>

          {/* Trust strip */}
          <ul className="mt-8 grid grid-cols-2 gap-3 text-sm text-white/85 sm:grid-cols-4">
            <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-white/70" aria-hidden="true" />10+ Years Experience</li>
            <li className="flex items-center gap-2"><Star className="h-4 w-4 text-white/70" aria-hidden="true" />Satisfaction Guaranteed</li>
            <li className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-white/70" aria-hidden="true" />Commercial Equipment</li>
            {SHOW_LICENSED_INSURED ? (
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-white/70" aria-hidden="true" />Licensed & Insured</li>
            ) : (
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-white/70" aria-hidden="true" />Fully Insured</li>
            )}
          </ul>
        </div>
      </section>
      <TopQuoteForm source="Concrete Sealing Top" defaultService="concrete" heading="Free Concrete Sealing Quote" />

      <SocialProofStrip />

      {/* WHAT SALT & FREEZE/THAW DO */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <h2 className="font-[var(--font-display)] text-3xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)] sm:text-4xl">
            What rock salt and freeze/thaw actually do
          </h2>
          <p className="mt-3 max-w-2xl text-[color:var(--brand-charcoal)]/80">
            Concrete looks solid, but it's porous. Water soaks in, freezes, and expands about 9% — popping the surface apart from the inside. Rock salt makes it worse by pulling moisture into the slab and forcing more freeze cycles.
          </p>

          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            <DamageCard
              Icon={Hammer}
              title="Spalling"
              desc="The top layer of concrete flakes off in sheets, exposing the rough aggregate underneath. This is the most common winter damage we see on Capital Region driveways."
              img={spallingImg}
              alt="Concrete spalling damage — surface flaking off in sheets"
            />
            <DamageCard
              Icon={Droplets}
              title="Pitting / Pop-outs"
              desc="Small, round holes and chipped-out aggregate pebbles across the surface. Each pit is a place where water froze and pushed a piece of concrete loose."
              img={pittingImg}
              alt="Concrete pitting damage — small round holes from freeze-thaw cycles"
            />
            <DamageCard
              Icon={Snowflake}
              title="Salt Scaling"
              desc="Thin, chalky layers peel away where de-icing salt has weakened the surface. It usually shows up first on the tire tracks and heavily walked areas."
              img={saltScalingImg}
              alt="Concrete salt scaling damage — thin surface layers peeling from road salt"
            />
          </div>
        </div>
      </section>

      {/* HONEST SCOPE */}
      <section className="bg-[var(--brand-light-gray)]">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <div className="flex flex-col items-start gap-5 rounded-2xl border-2 border-[color:var(--brand-red)]/30 bg-white p-6 shadow-sm sm:flex-row sm:items-start sm:p-8">
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[color:var(--brand-red)] text-white">
              <AlertTriangle className="h-8 w-8" aria-hidden="true" />
            </span>
            <div>
              <h3 className="font-[var(--font-display)] text-xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)] sm:text-2xl">
                What sealing can — and can't — do
              </h3>
              <p className="mt-3 text-sm text-[color:var(--brand-charcoal)]/85 sm:text-base">
                Sealing is prevention and containment, not resurfacing. If your concrete is already badly spalled, it needs repair or replacement — and <strong className="text-[var(--brand-deep-blue)]">we don't do resurfacing</strong>. What we do is knock off the loose, failing material so it stops spreading, clean the slab thoroughly, and seal the sound concrete that's left to keep water and salt out. If the slab is too far gone, we'll tell you honestly rather than sell you a seal job that won't hold up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <h2 className="font-[var(--font-display)] text-3xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)] sm:text-4xl">
            Our 4-step freeze/thaw prep
          </h2>
          <p className="mt-3 max-w-2xl text-[color:var(--brand-charcoal)]/80">
            We don't just spray and leave — and you don't wait days between visits. The sealer can be applied to a damp surface, so we clean, prep, and seal the same day.
          </p>

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            <ProcessCard
              step="1"
              title="Pre-treat & remove loose material"
              desc="Spot-treat organic staining and salt film. Hand-scrape or pressure-remove flaking, loose concrete so the damaged area can't keep spreading under the sealer."
            />
            <ProcessCard
              step="2"
              title="Commercial surface clean"
              desc="Even pass with a rotary surface cleaner across the whole slab. No wand marks, no zebra stripes, no missed spots."
            />
            <ProcessCard
              step="3"
              title="Blow off & flash dry — same day"
              desc="Break Wall applies to dry or damp concrete, so there's no multi-day wait. We blow off dust, sand, and debris, let the slab flash dry with no standing water, and move straight into sealing."
            />
            <ProcessCard
              step="4"
              title="Two coats of penetrating sealer"
              desc="Two coats applied wet-on-wet at right angles, then back-rolled to eliminate pooling. It soaks below the surface and cures inside the pores — no glossy film, no peeling. Dry to the touch in about an hour."
            />
          </div>
        </div>
      </section>

      {/* THE SEALER */}
      <section className="bg-[var(--brand-deep-blue)] text-white">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <div className="grid gap-8 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-[var(--font-display)] text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--brand-red)]">
                The Sealer
              </p>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl font-bold uppercase tracking-tight sm:text-4xl">
                Trident Break Wall
              </h2>
              <p className="mt-4 text-white/85">
                A high-performance silane/siloxane penetrating sealer that soaks in below the surface — no visible film, no gloss coat, no peeling. It repels water and salt, stays breathable so moisture and efflorescence don't get trapped, and inhibits mold, moss, and algae growth. Because it applies to dry <em>or damp</em> concrete, we clean and seal your project the same day.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/85">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Applies on dry <span className="mx-1 font-semibold text-white">or damp</span> surfaces — same-day clean and seal</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Invisible — won't change the look of your concrete</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Superior water repellency and salt blocking — protects against freeze/thaw damage</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Inhibits biological growth such as mold, moss & algae</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Breathable — won't trap moisture or efflorescence</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Non-film forming, penetrating chemistry — nothing to peel, flake, or turn cloudy</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> No silicone or wax components — dries transparent</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Water-based, low-VOC, EPA-compliant and environmentally friendly</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Temperature stable — freeze resistant to −60°F and stable to 180°F once cured</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Dry to the touch in about 60 minutes; foot and vehicle traffic in 2–4 hours</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Works on driveways, walks, steps, garage floors, stamped & exposed aggregate</li>
              </ul>
            </div>
            <div className="rounded-xl bg-white/5 p-6 backdrop-blur sm:p-8">
              <h3 className="font-[var(--font-display)] text-xl font-bold uppercase tracking-tight">
                Where we apply it
              </h3>
              <ul className="mt-4 grid gap-3 text-sm text-white/85 sm:grid-cols-2">
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-white/70" aria-hidden="true" /> Driveways</li>
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-white/70" aria-hidden="true" /> Walkways</li>
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-white/70" aria-hidden="true" /> Steps & stoops</li>
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-white/70" aria-hidden="true" /> Garage floors</li>
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-white/70" aria-hidden="true" /> Stamped concrete</li>
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-white/70" aria-hidden="true" /> Exposed aggregate</li>
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-white/70" aria-hidden="true" /> Concrete & clay pavers</li>
                <li className="flex items-center gap-2"><ThermometerSun className="h-4 w-4 text-white/70" aria-hidden="true" /> Retaining walls & block</li>
              </ul>
              <div className="mt-6 rounded-lg border border-white/15 bg-[color:var(--brand-red)]/15 p-4">
                <p className="font-[var(--font-display)] text-sm font-bold uppercase tracking-wide text-white">
                  Same-day project
                </p>
                <p className="mt-2 text-sm text-white/85">
                  Clean, prep, and seal in one visit. Since the sealer can go down on a damp surface, there's no waiting days for the slab to bake dry and no second trip.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BIOLOGICAL GROWTH INHIBITION */}
      {/* PROTECTION LEVELS */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <p className="font-[var(--font-display)] text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--brand-red)]">
            Choose Your Protection Level
          </p>
          <h2 className="mt-3 font-[var(--font-display)] text-3xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)] sm:text-4xl">
            Two Trident sealer options
          </h2>
          <p className="mt-3 max-w-2xl text-[color:var(--brand-charcoal)]/80">
            Both are invisible, breathable, penetrating sealers that block water and salt. The difference is how much protection you want and how long it lasts.
          </p>

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            <div className="rounded-xl border border-[color:var(--brand-deep-blue)]/10 bg-[var(--brand-light-gray)] p-6">
              <p className="font-[var(--font-display)] text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--brand-charcoal)]/60">
                Standard
              </p>
              <h3 className="mt-2 font-[var(--font-display)] text-2xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)]">
                Trident Break Wall
              </h3>
              <p className="mt-1 font-[var(--font-display)] text-lg font-bold uppercase tracking-tight text-[color:var(--brand-red)]">
                5-Year Protection
              </p>
              <p className="mt-3 text-sm text-[color:var(--brand-charcoal)]/85">
                Superior water-repelling and salt-blocking silane/siloxane sealer. Water-based, low-VOC, EPA compliant — the right call for most residential driveways, walks, and patios.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-[color:var(--brand-charcoal)]/85">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Superior water repellency & salt blocking</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Water-based, low-VOC, environmentally friendly</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Invisible, breathable, non-film forming</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Inhibits mold, moss & algae</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Applies on dry or damp concrete — same-day clean & seal</li>
              </ul>
            </div>

            <div className="relative rounded-xl border-2 border-[color:var(--brand-red)] bg-[var(--brand-deep-blue)] p-6 text-white shadow-sm">
              <span className="absolute -top-3 left-6 rounded-full bg-[color:var(--brand-red)] px-3 py-1 font-[var(--font-display)] text-xs font-bold uppercase tracking-wide text-white">
                Best protection
              </span>
              <p className="font-[var(--font-display)] text-xs font-bold uppercase tracking-[0.18em] text-white/60">
                Premium upgrade
              </p>
              <h3 className="mt-2 font-[var(--font-display)] text-2xl font-bold uppercase tracking-tight">
                Trident Sea Wall
              </h3>
              <p className="mt-1 font-[var(--font-display)] text-lg font-bold uppercase tracking-tight text-[color:var(--brand-red)]">
                7-Year Protection
              </p>
              <p className="mt-3 text-sm text-white/85">
                Trident's ultra water-repelling and salt-blocking sealer with advanced reactive chemistry and EZ Nano technology. Built for the surfaces that take the worst of Capital Region winters.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-white/85">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Ultimate protection against water & salt infiltration</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> EZ Nano technology, solvent-based deep penetration</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Prevents surface spalling from freeze/thaw cycles</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Invisible, breathable, won't trap efflorescence</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Longest reseal interval — best value over time</li>
              </ul>
              <button
                type="button"
                onClick={() => openQuoteModal({ services: ["concrete"], message: "Interested in the Trident Sea Wall 7-year sealer upgrade." })}
                className="mt-6 w-full rounded-md bg-[color:var(--brand-red)] px-5 py-3 font-[var(--font-display)] text-sm font-bold uppercase tracking-wide text-white transition hover:brightness-110"
              >
                Ask about the Sea Wall upgrade
              </button>
            </div>
          </div>

          <p className="mt-6 text-sm text-[color:var(--brand-charcoal)]/70">
            Not sure which one you need? We'll look at your slab, your salt exposure, and your sun/shade conditions and recommend the right level — no upsell pressure.
          </p>
        </div>
      </section>

      {/* BIOLOGICAL GROWTH INHIBITION */}
      <section className="bg-[var(--brand-light-gray)]">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <div className="grid gap-8 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-[var(--font-display)] text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--brand-red)]">
                Surface Protection
              </p>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)] sm:text-4xl">
                Inhibits Mold, Moss & Algae
              </h2>
              <p className="mt-4 text-[color:var(--brand-charcoal)]/85">
                Concrete that stays damp and porous is a breeding ground for mold, moss, and algae — especially on shaded walks, steps, and north-facing driveways. Trident Break Wall is a breathable, water-repellent sealer that stops moisture from sitting in the surface, which inhibits biological growth before it can take hold.
              </p>
              <p className="mt-3 text-[color:var(--brand-charcoal)]/85">
                That means fewer green or black stains, less slippery buildup, and less need for harsh chemical treatments year after year. It keeps the concrete cleaner and safer to walk on, while reducing the organic film that traps even more moisture against the slab.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-[color:var(--brand-charcoal)]/85">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Reduces moisture penetration where mold and algae grow</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Helps prevent green moss and black algae staining</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Makes annual cleanings faster and easier</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Keeps walkways less slippery and better looking year-round</li>
              </ul>
            </div>
            <div className="rounded-2xl border-2 border-[color:var(--brand-deep-blue)]/10 bg-white p-8 shadow-sm">
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--brand-deep-blue)] text-white">
                <Leaf className="h-8 w-8" aria-hidden="true" />
              </span>
              <h3 className="mt-5 font-[var(--font-display)] text-xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)]">
                The biological film problem
              </h3>
              <p className="mt-3 text-[color:var(--brand-charcoal)]/85">
                Mold, moss, and algae don't just stain concrete — they create a thin, absorbent layer that holds water against the surface. That trapped moisture accelerates freeze/thaw damage, spalling, and salt scaling. Sealing the concrete breaks that cycle by keeping the surface drier and the pores blocked.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW OFTEN */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <h2 className="font-[var(--font-display)] text-3xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)] sm:text-4xl">
            How often should concrete be sealed?
          </h2>
          <p className="mt-3 max-w-2xl text-[color:var(--brand-charcoal)]/80">
            Trident's technical data sheet recommends reapplication every 5–8 years depending on exposure. In the Capital Region, with heavy salt and freeze/thaw cycles, every 2–3 years is the practical maintenance cycle for most residential concrete. Heavy salt exposure shortens that timeline.
          </p>

          <div className="mt-8 grid gap-5 md:grid-cols-2">
            <div className="rounded-xl border border-[color:var(--brand-deep-blue)]/10 bg-[var(--brand-light-gray)] p-6">
              <h3 className="font-[var(--font-display)] text-xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)]">
                Typical reseal schedule
              </h3>
              <ul className="mt-4 space-y-2 text-sm text-[color:var(--brand-charcoal)]/85">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Trident recommends reapplication every 5–8 years depending on exposure</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Capital Region standard driveway: every 2–3 years in heavy salt/freeze-thaw conditions</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Aprons, steps, heavily salted areas: every 1–2 years</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Annual cleaning in between reseals</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Best applied in fall when the slab can dry and cure before winter</li>
              </ul>
            </div>
            <div className="rounded-xl border border-[color:var(--brand-deep-blue)]/10 bg-white p-6 shadow-sm">
              <h3 className="font-[var(--font-display)] text-xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)]">
                Is it time to reseal?
              </h3>
              <ul className="mt-4 space-y-2 text-sm text-[color:var(--brand-charcoal)]/85">
                <li className="flex items-start gap-2"><Calendar className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Water no longer beads on the surface</li>
                <li className="flex items-start gap-2"><Calendar className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> The slab darkens when it rains</li>
                <li className="flex items-start gap-2"><Calendar className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Surface looks dull, chalky, or grayish</li>
                <li className="flex items-start gap-2"><Calendar className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-red)]" aria-hidden="true" /> Fine grit or sand sweeps up off the concrete</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <ConcreteBeforeAfter />
      <StampedGallerySection />

      <ValueStack
        priceLabel="$50 off quote"
        heading="What a real clean & seal includes"
        intro="Here's everything that goes into a professional concrete cleaning and sealing job — and what you're getting."
        items={[
          { label: "Pre-treatment and loose-material removal", value: "Included" },
          { label: "Commercial rotary surface cleaning", value: "Included" },
          { label: "Same-day clean and seal — applies on damp concrete", value: "Included" },
          { label: "Two coats Trident Break Wall penetrating sealer", value: "Included" },
          { label: "Salt-blocking, water-repellent, invisible low-VOC finish", value: "Included" },
          { label: "Inhibits mold, moss & algae growth", value: "Included" },
          { label: "Capital Region winter-cycle experience", value: "Included" },
        ]}
      />

      <GuaranteeCallout />

      {/* FAQ */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-14 sm:py-20">
          <h2 className="font-[var(--font-display)] text-3xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)] sm:text-4xl">
            Quick questions
          </h2>
          <dl className="mt-8 divide-y divide-[color:var(--brand-deep-blue)]/10 border-y border-[color:var(--brand-deep-blue)]/10">
            {FAQS.map((item) => (
              <div key={item.q} className="py-5">
                <dt className="font-[var(--font-display)] text-lg font-bold uppercase tracking-tight text-[var(--brand-deep-blue)]">
                  {item.q}
                </dt>
                <dd className="mt-2 text-[color:var(--brand-charcoal)]/85">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[var(--brand-deep-blue)] text-white">
        <div className="mx-auto max-w-4xl px-4 py-14 text-center sm:py-20">
          <ScarcityPill className="mb-4" />
          <h2 className="font-[var(--font-display)] text-3xl font-bold uppercase leading-tight sm:text-5xl">
            Seal it before winter wins
          </h2>
          <p className="mt-3 text-white/85 sm:text-lg">
            Free quote. Most jobs quoted remotely. $50 off available when you mention this page.
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => openQuoteModal({ services: ["concrete"], message: "Interested in the $50 off concrete cleaning & sealing freeze/thaw special." })}
              className="inline-flex items-center justify-center rounded-md bg-[color:var(--brand-red)] px-7 py-4 text-base font-bold uppercase tracking-wide text-white shadow-lg transition hover:brightness-110 active:scale-[0.98]"
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-7 py-4 text-base font-bold uppercase tracking-wide text-[var(--brand-deep-blue)] shadow-lg transition hover:bg-white/90"
              data-analytics="call_click"
              data-source="concrete-freeze-thaw-final"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>
    <GuideStrip serviceSlug="concrete-cleaning-sealing" serviceName="Concrete Sealing" />
      <InlineQuoteSection source="Concrete Sealing" defaultService="concrete" />
    </main>
  );
}

function DamageCard({
  Icon,
  title,
  desc,
  img,
  alt,
}: {
  Icon: typeof Hammer;
  title: string;
  desc: string;
  img: string;
  alt: string;
}) {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-[color:var(--brand-deep-blue)]/10 bg-white shadow-sm">
      <div className="aspect-square overflow-hidden bg-gray-200">
        <img src={img} alt={alt} loading="lazy" width={640} height={640} className="h-full w-full object-cover" />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[var(--brand-deep-blue)] text-white">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <h3 className="font-[var(--font-display)] text-lg font-bold uppercase tracking-tight text-[var(--brand-deep-blue)]">
            {title}
          </h3>
        </div>
        <p className="mt-3 text-sm text-[color:var(--brand-charcoal)]/85">{desc}</p>
      </div>
    </div>
  );
}

function ProcessCard({ step, title, desc }: { step: string; title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-[color:var(--brand-deep-blue)]/10 bg-[var(--brand-light-gray)] p-6">
      <div className="flex items-start gap-4">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--brand-red)] text-sm font-bold text-white">
          {step}
        </span>
        <div>
          <h3 className="font-[var(--font-display)] text-lg font-bold uppercase tracking-tight text-[var(--brand-deep-blue)]">
            {title}
          </h3>
          <p className="mt-1 text-sm text-[color:var(--brand-charcoal)]/85">{desc}</p>
        </div>
      </div>
    </div>
  );
}

function ConcreteBeforeAfter() {
  return (
    <section className="bg-[var(--brand-light-gray)]">
      <div className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
        <div className="mb-6 max-w-2xl">
          <h2 className="font-[var(--font-display)] text-3xl font-bold uppercase tracking-tight text-[var(--brand-deep-blue)] sm:text-4xl">
            What sealed concrete looks like
          </h2>
          <p className="mt-3 text-[color:var(--brand-charcoal)]/80">
            Water beads up and rolls off instead of soaking into the slab — that's the sealer doing its job.
            Less water in the concrete means less freeze/thaw damage all winter.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="relative overflow-hidden rounded-lg bg-gray-200 shadow-lg aspect-[4/3]">
            <img src={beading1.url} alt="Water beading on a sealed concrete surface in the Capital Region" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <span className="absolute top-3 left-3 rounded-full bg-[#0B3D7A] px-3 py-1 text-xs font-bold uppercase tracking-widest text-white shadow">Sealed</span>
          </div>
          <div className="relative overflow-hidden rounded-lg bg-gray-200 shadow-lg aspect-[4/3]">
            <img src={beading2.url} alt="Rain water beading on concrete protected with a penetrating sealer" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <span className="absolute top-3 right-3 rounded-full bg-[#C8102E] px-3 py-1 text-xs font-bold uppercase tracking-widest text-white shadow">Water Repelled</span>
          </div>
        </div>
      </div>
    </section>
  );
}
